/**
 * The ranger's firearms: the carbine (02 §7) and the suppressed pistol (04 §6). Both put a
 * round down the aim - or, under the Classic scheme, onto the man the aim was meant for
 * (`aimAssist.ts`) - with a spread that depends on what he is doing and the state he is in.
 */

import { makeNoise } from '../alert';
import type { Ctx } from '../ctx';
import { cos, PI, sin } from '../math';
import { spawnBullet } from '../projectiles';
import { CARBINE, GEAR } from '../tuning';
import { assistedAim } from './aimAssist';

const DEG = PI / 180;

/**
 * Alert level from which Hothead's "when enemies are alerted" bites. R6: it was 2 - gunfire or a
 * blast - and 1 is a real alerted state the game names in its own words: a body found raises it, a
 * searchlight catching him raises it, and the High Alert modifier's card says in so many words that
 * the garrison STARTS there. A Hothead flying High Alert read two cards that between them said he
 * would shoot worse, and he did not. The gate came to the words.
 *
 * It is here and not in `tuning.ts` for the reason `alert.ts` gives for its own minute: that file
 * is at the 300-line limit to the line, and splitting it by concern is not this round's job.
 */
const ALERTED = 1;

/** What wounds and nerves do to any shot: +15 % a wound, and Hothead's shake once the garrison is up (04 §4). */
function unsteadiness(ctx: Ctx): number {
  const w = ctx.world;
  // R5: `aimSpread` is the nerves a man carries everywhere - Green and Shaken. It used to ride on
  // `aimSpreadStill`, so a green recruit shot like a veteran the moment he started walking (ROUND5 §3.6).
  return (1 + w.ranger.wounds * CARBINE.spreadPerWound) * w.mods.aimSpread
    * (w.alert >= ALERTED ? w.mods.alertedSpread : 1);
}

/**
 * Ticks until the carbine may fire again. The cooldown can only be whole ticks, so the fraction each
 * shot is rounded down by is carried into the next one: at `fireRate` 1.1 that is 5.4545 ticks a shot,
 * paid as 5, 5, 6, 5, 6..., averaging exactly the rate promised. `round()` alone gave a flat 5 - Hothead's
 * "+10 % fire rate" fired +20 % (ROUND5 §3.1). At rate 1 the carry is exactly 0 and nothing moves at all.
 */
function shotCooldown(r: { fireCarry: number }, fireRate: number): number {
  const owed = CARBINE.ticksPerShot / fireRate + r.fireCarry;
  const ticks = Math.max(1, Math.floor(owed));
  // Never carry a debt: a rate fast enough to want less than a tick a shot still costs one.
  r.fireCarry = Math.max(0, owed - ticks);
  return ticks;
}

/** Two draws, whatever the spread, so a perk that zeroes it does not shift the rest of the mission's dice. */
function scatter(ctx: Ctx, spreadDeg: number): number {
  return (ctx.rng.float() + ctx.rng.float() - 1) * spreadDeg * DEG;
}

/** Fire is held with the carbine up: a round, a reload, or the click of an empty weapon. */
export function fireCarbine(ctx: Ctx, firePressed: boolean): void {
  const w = ctx.world;
  const r = w.ranger;
  const mods = w.mods;
  if (r.inventory.roundsInMag > 0) {
    const base = r.stance === 'crawl' ? CARBINE.spreadProne
      : !r.moving ? CARBINE.spreadStill
      : r.running ? CARBINE.spreadRun : CARBINE.spreadWalk;
    const steady = r.moving ? 1 : mods.aimSpreadStill;
    // Controlled Pairs: the first rounds of a burst go exactly where he points.
    const free = r.burstRounds < mods.burstFreeRounds;
    const range = CARBINE.range * mods.carbineRange;
    // The muzzle points where the round goes: under aim assist that is at the man, not down the crosshair.
    const aim = assistedAim(ctx, range);
    const a = aim + scatter(ctx, free ? 0 : base * steady * unsteadiness(ctx));
    spawnBullet(ctx, true, r.x + sin(aim) * 0.6, r.z + cos(aim) * 0.6, sin(a), cos(a), range, -1, false);
    r.inventory.roundsInMag--;
    r.burstRounds++;
    r.fireCooldown = shotCooldown(r, mods.fireRate);
    r.sinceShot = 0;
    makeNoise(ctx, r.x, r.z, CARBINE.noise, 'gunfire');
  } else if (r.inventory.magazines > 0) {
    r.reloadTicks = Math.round(CARBINE.reloadTicks * mods.reloadTime);
    ctx.events.push({ type: 'reload' });
  } else if (firePressed) {
    ctx.events.push({ type: 'dryFire' });
  }
}

/**
 * One press, one round. Its cough carries a few metres and is heard like a footstep: the men
 * beside him turn to look, the garrison never knows (02 §6.3). The last round puts the carbine back in his hands.
 */
export function firePistol(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  if (r.gear.pistolRounds <= 0) {
    ctx.events.push({ type: 'dryFire' });
    r.weapon = 'carbine';
    return;
  }
  const base = r.moving && r.stance !== 'crawl' ? GEAR.pistolSpreadMoving : GEAR.pistolSpreadStill;
  const steady = r.moving ? 1 : w.mods.aimSpreadStill;
  const aim = assistedAim(ctx, GEAR.pistolRange);
  const a = aim + scatter(ctx, base * steady * unsteadiness(ctx));
  spawnBullet(ctx, true, r.x + sin(aim) * 0.5, r.z + cos(aim) * 0.5, sin(a), cos(a), GEAR.pistolRange, -1, false, true);
  r.gear.pistolRounds--;
  r.fireCooldown = GEAR.pistolTicksPerShot;
  r.sinceShot = 0;
  makeNoise(ctx, r.x, r.z, GEAR.pistolNoise, 'step');
  if (r.gear.pistolRounds === 0) r.weapon = 'carbine';
}
